/**
 * UploadBaseModal — nova versão de base (INEP/IBGE): o Administrador escolhe o
 * arquivo; o modal pede a URL pré-assinada, envia o arquivo direto ao bucket
 * temporário, dispara a carga e acompanha até concluir (C-M3, contrato M3).
 *
 * Antes o modal pedia a chave S3 de um arquivo "já enviado" ao prefixo temporário
 * — nenhuma tela fazia esse envio — e não acompanhava a carga (o 202 não trazia id).
 */

import { FormEvent, useState } from 'react';
import { useEnviarBase, useEstadoCargaBase } from '../hooks/useBasesReferencia';
import { TipoBaseUpload } from '../services/bases-types';

export interface UploadBaseModalProps {
  readonly tipo: TipoBaseUpload;
  readonly onFechar: () => void;
  readonly onIniciada?: () => void;
  /** Intervalo do polling do estado da carga (ms). */
  readonly intervaloPollingMs?: number;
}

const ACEITA: Readonly<Record<TipoBaseUpload, string>> = {
  inep: '.csv,text/csv',
  ibge: '.geojson,.json,application/geo+json,application/json',
};

export function UploadBaseModal({ tipo, onFechar, onIniciada, intervaloPollingMs }: UploadBaseModalProps): JSX.Element {
  const { enviar, enviando, erro } = useEnviarBase();
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [notas, setNotas] = useState('');
  const [erroLocal, setErroLocal] = useState<string | null>(null);
  const [cargaId, setCargaId] = useState<string | null>(null);
  const estado = useEstadoCargaBase(cargaId, intervaloPollingMs);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setErroLocal(null);
    if (!arquivo) {
      setErroLocal('Escolha o arquivo da base.');
      return;
    }
    try {
      const resp = await enviar(tipo, arquivo, notas.trim() || undefined);
      setCargaId(resp.cargaId);
      onIniciada?.();
    } catch {
      /* erro do hook cobre o feedback */
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label={`Nova versão ${tipo.toUpperCase()}`}>
      <div className="modal">
        <h2>Nova versão — {tipo.toUpperCase()}</h2>
        <p className="muted">
          {tipo === 'inep' ? 'Catálogo de escolas em CSV.' : 'Malha de municípios em GeoJSON.'} A importação roda
          em segundo plano e a nova versão fica ativa ao concluir.
        </p>

        {cargaId ? (
          <div>
            {!estado || estado.status === 'processando' ? (
              <p className="notice notice-info">Carregando a versão {estado?.versao ?? ''}…</p>
            ) : estado.status === 'concluida' ? (
              <p className="notice notice-ok">Carga concluída: versão {estado.versao} ativa.</p>
            ) : (
              <p className="notice">A carga falhou. Confira o arquivo e tente novamente.</p>
            )}
            <div className="row-actions">
              <button type="button" className="btn btn-primary" onClick={onFechar}>
                Fechar
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            <div className="form-field">
              <label htmlFor="arquivoBase">Arquivo da base</label>
              <input
                id="arquivoBase"
                type="file"
                accept={ACEITA[tipo]}
                onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="notas">Notas (opcional)</label>
              <input id="notas" type="text" value={notas} onChange={(e) => setNotas(e.target.value)} />
            </div>

            {erroLocal && <p className="notice">{erroLocal}</p>}
            {erro && <p className="notice">Não foi possível enviar o arquivo. Tente novamente.</p>}

            <div className="row-actions">
              <button type="submit" className="btn btn-primary" disabled={enviando}>
                {enviando ? 'Enviando…' : 'Enviar e carregar'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={onFechar}>
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
